import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { reuseViewLabels, statusIsActive } from "../graphComponents/reuseTimeline.js";

export const formatTopFlyerLabel = (vehicle) =>
  vehicle && Number(vehicle.num_launches || 0) > 0 ? `${vehicle.name}-${vehicle.num_launches}` : null;

export const TopFlyerLink = ({ vehicle, detailPath }) => {
  const label = formatTopFlyerLabel(vehicle);

  if (!label || !vehicle?.id) {
    return <strong>—</strong>;
  }

  return (
    <Link className="vl-top-flyer-link" to={`/${detailPath}/${vehicle.id}/`}>
      {label}
    </Link>
  );
};

export const ReuseTimelinePanel = ({ copy, reuseChart, reuseView, setReuseView, loading, detailPath }) => (
  <section className="bt-panel bt-reuse-panel vl-reuse-panel">
    <div className="bt-panel-head">
      <div>
        <p className="bt-section-label">{copy.timelineLabel}</p>
        <h2 className="bt-panel-title">{copy.timelineTitle}</h2>
        <p className="bt-panel-subnote">{reuseViewLabels[reuseView].helper}</p>
      </div>
      <div className="bt-reuse-tools">
        <div className="bt-mini-legend">
          <span>Active</span>
          <span className="retired">Retired</span>
        </div>
        <div className="bt-view-toggle" aria-label="Reuse timeline view">
          {Object.entries(reuseViewLabels).map(([key, value]) => (
            <button
              className={reuseView === key ? "active" : ""}
              key={key}
              type="button"
              onClick={() => setReuseView(key)}
            >
              {value.label}
            </button>
          ))}
        </div>
      </div>
    </div>
    {loading ? (
      <div className="vl-reuse-loading">
        <span className="loading-indicator" />
        <p>Loading flight history…</p>
      </div>
    ) : reuseChart?.rows?.length ? (
      <div className="bt-reuse-chart">
        <div className="bt-reuse-inner">
          {reuseChart.rows.map((vehicle) => (
            <div
              className={`bt-reuse-row ${vehicle.active ? "active" : "retired"}`}
              style={{ "--last-x": vehicle.dots[vehicle.dots.length - 1]?.x || 0 }}
              key={vehicle.id}
            >
              <Link className="bt-reuse-name" to={`/${detailPath}/${vehicle.id}/`}>
                {vehicle.name}
              </Link>
              <span className="bt-reuse-lane"></span>
              {vehicle.dots.map((dot, index) => (
                <i
                  className="bt-reuse-dot"
                  style={{ "--x": dot.x }}
                  title={dot.title}
                  aria-label={dot.title}
                  key={`${vehicle.id}-${index}`}
                ></i>
              ))}
              <span className="bt-reuse-count" style={{ "--x": vehicle.dots[vehicle.dots.length - 1]?.x || 0 }}>
                x{vehicle.count}
              </span>
            </div>
          ))}
          <div className="bt-reuse-axis">
            {reuseChart.axisLabels.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>
        </div>
      </div>
    ) : (
      <div className="vl-reuse-empty">
        <strong>No flight history yet</strong>
        <span>Reuse marks will appear once vehicles have completed missions.</span>
      </div>
    )}
  </section>
);

export const FleetSnapshotStrip = ({ vehicles, copy }) => {
  const statusBuckets = useMemo(() => {
    const counts = { Active: 0, Retired: 0, Expended: 0, Lost: 0, Other: 0 };

    vehicles.forEach((vehicle) => {
      const status = String(vehicle.status || "").toUpperCase();
      if (statusIsActive(status)) {
        counts.Active += 1;
      } else if (status.includes("RETIRED")) {
        counts.Retired += 1;
      } else if (status.includes("EXPENDED")) {
        counts.Expended += 1;
      } else if (status.includes("LOST")) {
        counts.Lost += 1;
      } else {
        counts.Other += 1;
      }
    });

    return Object.entries(counts).filter(([, count]) => count > 0);
  }, [vehicles]);

  const flown = vehicles.filter((vehicle) => Number(vehicle.num_launches || 0) > 0);
  const totalFlights = flown.reduce((sum, vehicle) => sum + Number(vehicle.num_launches || 0), 0);
  const avgFlights = flown.length ? Math.round((totalFlights / flown.length) * 10) / 10 : 0;
  const topFlyer = [...vehicles].sort((a, b) => Number(b.num_launches || 0) - Number(a.num_launches || 0))[0];

  return (
    <section className="vl-snapshot" aria-label="Fleet snapshot">
      <div className="vl-snapshot-head">
        <p className="bt-section-label">Fleet snapshot</p>
        <span>
          {vehicles.length} {copy.vehicleNoun}
          {flown.length ? ` · ${flown.length} flown` : ""}
        </span>
      </div>
      <div className="vl-snapshot-grid">
        {statusBuckets.map(([label, count]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{count}</strong>
          </div>
        ))}
        <div>
          <span>Avg flights</span>
          <strong>{avgFlights || "—"}</strong>
        </div>
        <div>
          <span>Top flyer</span>
          <TopFlyerLink vehicle={topFlyer} detailPath={copy.detailPath} />
        </div>
      </div>
    </section>
  );
};
